import { fetchProductInfoAPI } from '@/api/storeManagement/shelfPlanogram';
import { useAssociationState } from './context';
import { useAppContext } from '@/provider/contexts';
import { useTranslation } from 'react-i18next';
import { fetchTagLayoutDetailsAPI, pushDevicesToProductsAPI } from '@/api/storeManagement/device';
import {
    ASSOCIATE_PAIRING_MODE,
    AssociationPayload,
    AssociateModeRelations,
    ASSOCIATION_MODE,
    ImageInfo,
    PAIRING_MODE,
    ShelfInfo,
} from '@/types/pages/storeManagement/device/associate';
import {
    deriveImageConfig,
    deriveMultiPayloads,
    deriveShelfConfig,
    deriveSinglePayloads,
} from '@/utils/associateUtils';
import { ProductData } from '@/types/pages/storeManagement/shelfPlanogram';
import { useEffect, useRef } from 'react';
import { isServiceUnavailable, SERVICE_UNAVAILABLE_MESSAGE } from '@/utils/deviceActionUtils';

type UseAssociationHookOptions = {
    onUpdateSuccess?: () => void;
};

type ConfirmFlowResult = 'DONE' | 'NO_CAMPAIGN_ASSETS' | 'PRODUCT_INFO_UNAVAILABLE';

export const useAssociationHook = ({ onUpdateSuccess }: UseAssociationHookOptions = {}) => {
    const { t } = useTranslation('common');
    const { manageStoreId, setNotification } = useAppContext();
    const { state, dispatch } = useAssociationState();
    const onUpdateSuccessRef = useRef(onUpdateSuccess);

    useEffect(() => {
        onUpdateSuccessRef.current = onUpdateSuccess;
    }, [onUpdateSuccess]);

    const initialize = async (macs: string[]) => {
        try {
            dispatch({ type: 'SET_LOADING', payload: true });
            const {
                data: {
                    isSuccess,
                    data: { device, category },
                    message,
                },
            } = await fetchTagLayoutDetailsAPI(manageStoreId as number, macs);
            if (isSuccess) {
                dispatch({ type: `SET_INIT`, payload: { device, category } });
                return;
            }
            throw new Error(message);
        } catch (error: unknown) {
            if (error instanceof Error) {
                setNotification({ message: error?.message || t('Failed to Fetch Info'), severity: 'error' });
                return;
            }
            console.error('Error Fetch tag:', error);
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    };

    const fetchProductInfo = async (products: string[]) => {
        try {
            const {
                data: { data, isSuccess, message },
            } = await fetchProductInfoAPI(manageStoreId as number, products);
            if (isSuccess) {
                return data.results;
            }
            throw new Error(message);
        } catch (error: unknown) {
            if (error instanceof Error) {
                setNotification({ message: error?.message || t('Failed to Fetch Product Info'), severity: 'error' });
                return null;
            }
            console.error('Error Fetch Product Info:', error);
            return null;
        }
    };

    const prepareSingleProductInfo = async (barcode: string | null) => {
        if (!barcode) {
            dispatch({
                type: 'SET_SHELF_INFO',
                payload: {
                    openFeature: false,
                    options: [],
                    shouldOpenDialog: false,
                    relations: {},
                },
            });
            dispatch({ type: 'SET_IMAGE_INFO', payload: { openFeature: false, options: [], relations: {} } });
            dispatch({ type: 'SET_NO_CAMPAIGN_WORDING', payload: '' });
            return;
        }
        dispatch({ type: 'SET_PRODUCT_LOADING', payload: true });
        const productInfo = (await fetchProductInfo([barcode])) as ProductData['results'] | null;
        dispatch({ type: 'SET_PRODUCT_LOADING', payload: false });
        if (!productInfo) return;

        const shelfInfo = deriveShelfConfig(productInfo, state.planogramConfig);
        const imageInfo = deriveImageConfig(productInfo, state.campaignEnable);
        dispatch({ type: 'SET_SHELF_INFO', payload: shelfInfo });
        dispatch({ type: 'SET_IMAGE_INFO', payload: imageInfo });
    };

    const handleMultiProfileConfirm = async (products: string[]) => {
        const productInfo = (await fetchProductInfo(products)) as ProductData['results'];
        if (!productInfo) return 'PRODUCT_INFO_UNAVAILABLE' as ConfirmFlowResult;
        const shelfInfo = deriveShelfConfig(productInfo, state.planogramConfig);
        const imageInfo = deriveImageConfig(productInfo, state.campaignEnable);
        await handleNextFlow(shelfInfo, imageInfo, ASSOCIATION_MODE.MULTI);
    };

    const handleNextFlow = async (
        shelfInfo: Partial<ShelfInfo>,
        imageInfo: Partial<ImageInfo>,
        mode: ASSOCIATION_MODE,
    ) => {
        if (state.selectedPairingMode === ASSOCIATE_PAIRING_MODE.NORMAL) {
            if (shelfInfo.openFeature) {
                await handleShelfFlow(shelfInfo, mode);
                return;
            }

            if (mode === ASSOCIATION_MODE.SINGLE) {
                await handleAssociateSingle();
                return;
            }
            dispatch({ type: 'SET_DIALOG', payload: { key: 'multiProfile', open: false } });
            dispatch({ type: 'SET_MULTI_PROFILE_FINISH' });
            return;
        }

        if (imageInfo.openFeature) {
            dispatch({ type: 'SET_DIALOG', payload: { key: 'image', open: true } });
            dispatch({ type: 'SET_IMAGE_INFO', payload: imageInfo });
            return;
        }
        dispatch({ type: 'SET_NO_CAMPAIGN_WORDING', payload: t('No campaign assets found for this product.') });
    };

    const handleShelfFlow = async (shelfInfo: Partial<ShelfInfo>, mode: ASSOCIATION_MODE) => {
        if (shelfInfo.shouldOpenDialog) {
            dispatch({ type: 'SET_DIALOG', payload: { key: 'shelf', open: true } });
            dispatch({ type: 'SET_SHELF_INFO', payload: shelfInfo });
            return;
        }

        dispatch({ type: 'SET_DIALOG', payload: { key: 'pairingMode', open: false } });
        if (mode === ASSOCIATION_MODE.SINGLE) {
            await handleAssociateSingle({
                source: PAIRING_MODE.SHELF,
                ...shelfInfo.relations,
            });
            return;
        }

        dispatch({ type: 'SET_DIALOG', payload: { key: 'multiProfile', open: false } });
        dispatch({ type: 'SET_MULTI_SHELF_RELATIONS', payload: shelfInfo.relations as AssociateModeRelations });
    };

    const runAssociatePayloads = async (payloads: Partial<AssociationPayload>[]) => {
        for (const payload of payloads) {
            try {
                const { data } = await pushDevicesToProductsAPI({
                    store_id: manageStoreId as number,
                    ...payload,
                });
                if (!data.isSuccess) {
                    setNotification({
                        message: isServiceUnavailable(data)
                            ? t(SERVICE_UNAVAILABLE_MESSAGE)
                            : data.message || t('Failed to associate device(s).'),
                        severity: 'error',
                    });
                }
            } catch (error: unknown) {
                const errorMessage = error instanceof Error ? error.message : 'Unknown error';
                console.error('Error Associate Device:', errorMessage);
                setNotification({ message: t('Failed to associate device(s).'), severity: 'error' });
            }
        }
    };

    const handleAssociateSingle = async (relations?: AssociateModeRelations) => {
        dispatch({ type: 'SET_ASSOCIATE_LOADING', payload: true });
        try {
            const payloads = deriveSinglePayloads(state, relations);
            await runAssociatePayloads(payloads);
        } finally {
            dispatch({ type: 'SET_CLEAR' });
            await onUpdateSuccessRef.current?.();
        }
    };

    const handleAssociateMulti = async () => {
        dispatch({ type: 'SET_ASSOCIATE_LOADING', payload: true });
        try {
            const payloads = deriveMultiPayloads(state);
            await runAssociatePayloads(payloads);
        } finally {
            dispatch({ type: 'SET_CLEAR' });
            await onUpdateSuccessRef.current?.();
        }
    };

    return {
        initialize,
        fetchProductInfo,
        prepareSingleProductInfo,
        handleMultiProfileConfirm,
        handleShelfFlow,
        handleNext: handleNextFlow,
        handleAssociateSingle,
        handleAssociateMulti,
    };
};
